import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { Dropdown } from 'primereact/dropdown'
import { Calendar } from 'primereact/calendar'
import Button from '../../../components/ui/Button'
import { parseISODate, toISODate } from '../../../lib/datetime'
import {
  ASSIGNEE_TYPE,
  isMainResponsible,
  isSecondaryResponsible,
  mainResponsibleOf,
  personInputToMinutePerson,
  secondaryResponsiblesOf,
  withAssigneeType,
  type ActionFormRow,
  type ActionLevels,
  type PersonInput,
  type TaskClassification,
} from '../types'
import ActionPeopleEditor from './ActionPeopleEditor'
import SinglePersonEditor from './SinglePersonEditor'
import TaskClassificationFields from './TaskClassificationFields'

interface Props {
  visible: boolean
  /** Düzenlenen/eklenen satırın başlangıç değeri (yeni → boş satır). */
  initial: ActionFormRow
  /** true → yeni ekleme (başlık/aksiyon etiketi), false → düzenleme. */
  isNew: boolean
  /** Aciliyet/Önem Derecesi picklist seçenekleri (REFERANS). */
  levels: ActionLevels | undefined
  /** Termin alt sınırı (toplantı tarihi); termin toplantıdan önce olamaz (analiz 1.10.5). */
  minDeadline?: Date
  onSave: (row: ActionFormRow) => void
  onHide: () => void
}

/**
 * Aksiyon Ekle/Düzenle popup'ı (analiz 1.10) — girişler karmaşık satır kartları yerine dialog içinde toplanır.
 * Tanım, Açıklama, <b>Aciliyet Derecesi</b> ve <b>Önem Derecesi</b> (seçilebilir referans; toplantıya bağlı
 * aksiyonda zorunlu), <b>Sorumlu</b> (çoklu), Termin. Kaydet → zorunlu alanlar
 * doğrulanır, satır döner (Sorumlu satırla birlikte {@code actions[]} senkronunda kaydedilir).
 */
export default function ActionFormDialog({
  visible,
  initial,
  isNew,
  levels,
  minDeadline,
  onSave,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<ActionFormRow>(initial)
  const [showErrors, setShowErrors] = useState(false)
  // Aynı kişinin iki rolde seçilmesi denendi mi? Hangi bölümde uyarı gösterileceğini tutar (2026-08-12).
  const [roleConflict, setRoleConflict] = useState<'main' | 'secondary' | null>(null)

  // Her açılışta başlangıç değerinden temiz başla.
  useEffect(() => {
    if (visible) {
      setDraft(initial)
      setShowErrors(false)
      setRoleConflict(null)
    }
  }, [visible, initial])

  /**
   * Kayıtlı görevin hedef teslim tarihi bu kullanıcıya kapalı mı (130 — `ACTION_DEADLINE_UPDATE` ∨
   * oluşturan, sunucudan; gelmezse kapalı)? Kaydedilmemiş satır (id yok — yeni ya da Excel'den
   * aktarılmış) kurala tabi değildir: ilk tarih her zaman girilir. Sunucu senkronda aynı kuralla 403 verir.
   */
  const deadlineLocked = initial.id != null && initial.deadlineEditable !== true

  const invalid = {
    name: draft.name.trim() === '',
    description: draft.description.trim() === '',
    // Toplantıya bağlı aksiyonda Aciliyet/Önem Derecesi zorunlu (seçilmeli).
    urgency: draft.urgencyLevelId == null,
    importance: draft.importanceLevelId == null,
    deadline: draft.deadline === '',
    // Ana Sorumlu zorunlu (2026-08-12) — tekil. Yan Sorumlu opsiyoneldir.
    mainResponsible: mainResponsibleOf(draft.assignees) == null,
  }
  const hasError = Object.values(invalid).some(Boolean)

  // Sorumlular tek listede (payload replace-all) tutulur; rol alanıyla Ana/Yan olarak ayrışırlar.
  // Formda YALNIZ Ana ve Yan bölümleri gösterilir (kullanıcı kuralı 2026-08-12). 2026-08-12 öncesi
  // ROLSÜZ sorumlular ekranda çıkmaz ama `draft.assignees` içinde durur → payload'a girer ve
  // silinmezler; aksi halde eski bir aksiyonu düzenlemek sorumlularını sessizce yok ederdi.
  const mainResponsible = mainResponsibleOf(draft.assignees)
  const secondaryResponsibles = secondaryResponsiblesOf(draft.assignees)
  // Stratejik Hedef / Katkı (127) — tanımsız alan gösterimde boş liste olur; yazarken yalnız DEĞİŞEN boyut
  // satıra geçer (bkz. changedDimensions).
  const classificationValue: TaskClassification = {
    strategicObjectives: draft.strategicObjectives ?? [],
    contributions: draft.contributions ?? [],
  }

  /**
   * Ana Sorumlu ata/değiştir: eski Ana listeden çıkar, yeni kişi Ana rolüyle eklenir (tekillik korunur).
   * Kişi <b>Yan Sorumlu</b> ise seçim <b>reddedilir</b> (kullanıcı kuralı 2026-08-12): sistem rolü sessizce
   * taşımaz, önce Yan Sorumlu listesinden çıkarılmalıdır.
   */
  const setMainResponsible = (p: PersonInput) => {
    if (isSecondaryResponsible(draft.assignees, p.employeeId)) {
      setRoleConflict('main')
      return
    }
    setRoleConflict(null)
    setDraft((d) => ({
      ...d,
      assignees: [
        ...d.assignees.filter(
          (x) => x.assigneeTypeId !== ASSIGNEE_TYPE.MAIN && x.employeeId !== p.employeeId,
        ),
        withAssigneeType(personInputToMinutePerson(p), ASSIGNEE_TYPE.MAIN),
      ],
    }))
  }
  /** Yan Sorumlu ekle — sicil bazında tekil; Ana Sorumlu seçilirse aynı kuralın simetriği: hata. */
  const addSecondary = (p: PersonInput) => {
    if (isMainResponsible(draft.assignees, p.employeeId)) {
      setRoleConflict('secondary')
      return
    }
    setRoleConflict(null)
    setDraft((d) =>
      d.assignees.some((x) => x.employeeId === p.employeeId)
        ? d
        : {
            ...d,
            assignees: [
              ...d.assignees,
              withAssigneeType(personInputToMinutePerson(p), ASSIGNEE_TYPE.SECONDARY),
            ],
          },
    )
  }
  const removeAssignee = (employeeId: string) => {
    // Çakışma uyarısının çözümü "kişiyi diğer rolden çıkar"dır → çıkarınca uyarı da kalkmalı; aksi halde
    // kullanıcı sorunu çözdüğü hâlde ekranda hâlâ engellendiğini okur.
    setRoleConflict(null)
    setDraft((d) => ({ ...d, assignees: d.assignees.filter((x) => x.employeeId !== employeeId) }))
  }

  const submit = () => {
    if (hasError) {
      setShowErrors(true)
      return
    }
    onSave({
      ...draft,
      name: draft.name.trim(),
      description: draft.description.trim(),
    })
  }

  const footer = (
    <div className="ui-dialog-footer">
      <Button icon="pi pi-check" label={t('actions.editor.save')} onClick={submit} />
    </div>
  )

  return (
    <Dialog
      visible={visible}
      header={isNew ? t('actions.editor.addTitle') : t('actions.editor.editTitle')}
      footer={footer}
      onHide={onHide}
      modal
      dismissableMask
      draggable={false}
      style={{ width: '38rem', maxWidth: '95vw' }}
    >
      <div className="act-form">
        <label className="act-field act-field--full">
          <span className="ui-field-label">
            {t('actions.form.name')} <span className="ui-req">*</span>
          </span>
          <InputText
            value={draft.name}
            maxLength={250}
            placeholder={t('actions.form.namePlaceholder')}
            className={showErrors && invalid.name ? 'p-invalid' : undefined}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          />
          {showErrors && invalid.name && (
            <span className="act-form-err">{t('actions.form.required')}</span>
          )}
        </label>

        <label className="act-field act-field--full">
          <span className="ui-field-label">
            {t('actions.form.description')} <span className="ui-req">*</span>
          </span>
          <InputTextarea
            value={draft.description}
            rows={3}
            autoResize
            maxLength={2000}
            className={showErrors && invalid.description ? 'p-invalid' : undefined}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
          />
          {showErrors && invalid.description && (
            <span className="act-form-err">{t('actions.form.required')}</span>
          )}
        </label>

        <div className="act-form-grid">
          {/* Aciliyet Derecesi · Önem Derecesi (seçilebilir referans; ikisi de zorunlu). */}
          <div className="act-form-scores">
            <label className="act-field">
              <span className="ui-field-label">
                {t('actions.form.urgency')} <span className="ui-req">*</span>
              </span>
              <Dropdown
                value={draft.urgencyLevelId}
                options={levels?.urgencyLevels ?? []}
                optionValue="id"
                optionLabel="name"
                placeholder={t('actions.form.selectPlaceholder')}
                className={showErrors && invalid.urgency ? 'p-invalid' : undefined}
                onChange={(e) => setDraft((d) => ({ ...d, urgencyLevelId: e.value ?? null }))}
              />
              {showErrors && invalid.urgency && (
                <span className="act-form-err">{t('actions.form.required')}</span>
              )}
            </label>

            <label className="act-field">
              <span className="ui-field-label">
                {t('actions.form.importance')} <span className="ui-req">*</span>
              </span>
              <Dropdown
                value={draft.importanceLevelId}
                options={levels?.importanceLevels ?? []}
                optionValue="id"
                optionLabel="name"
                placeholder={t('actions.form.selectPlaceholder')}
                className={showErrors && invalid.importance ? 'p-invalid' : undefined}
                onChange={(e) => setDraft((d) => ({ ...d, importanceLevelId: e.value ?? null }))}
              />
              {showErrors && invalid.importance && (
                <span className="act-form-err">{t('actions.form.required')}</span>
              )}
            </label>
          </div>

          {/* Ana Sorumlu — TEKİL ve zorunlu. Seçici açık kalır (disableWhenSet yok): yeni kişi seçilince
              eskisinin yerini alır. "Temizle" yoktur (canClear=false) çünkü alan boş bırakılamaz. */}
          <div className="act-field act-field--full">
            <span className="ui-field-label">
              {t('actions.detail.mainResponsible.title')} <span className="ui-req">*</span>
            </span>
            <SinglePersonEditor
              person={mainResponsible}
              editable
              canClear={false}
              onSet={setMainResponsible}
              onClear={() => {}}
              placeholder={t('actions.detail.mainResponsible.placeholder')}
              removeLabel={t('actions.detail.assignees.remove')}
            />
            {showErrors && invalid.mainResponsible && (
              <span className="act-form-err">{t('actions.detail.mainResponsible.required')}</span>
            )}
            {/* Yan Sorumlu'yu Ana yapmaya çalışmak: seçim uygulanmaz, gerekçesi burada söylenir. */}
            {roleConflict === 'main' && (
              <span className="act-form-err">
                {t('actions.detail.roleConflict.mainIsSecondary')}
              </span>
            )}
          </div>

          {/* Yan Sorumlu — çoklu ve opsiyonel; her biri için otomatik alt aksiyon üretilir. */}
          <div className="act-field act-field--full">
            <span className="ui-field-label">
              {t('actions.detail.secondaryResponsibles.title')}
            </span>
            <ActionPeopleEditor
              people={secondaryResponsibles}
              editable
              onAdd={addSecondary}
              onRemove={removeAssignee}
              placeholder={t('actions.detail.secondaryResponsibles.placeholder')}
              removeLabel={t('actions.detail.assignees.remove')}
            />
            {roleConflict === 'secondary' && (
              <span className="act-form-err">
                {t('actions.detail.roleConflict.secondaryIsMain')}
              </span>
            )}
          </div>

          <label className="act-field act-field--full">
            <span className="ui-field-label">
              {t('actions.form.deadline')} {!deadlineLocked && <span className="ui-req">*</span>}
            </span>
            <Calendar
              value={parseISODate(draft.deadline)}
              minDate={minDeadline}
              dateFormat="dd.mm.yy"
              showIcon
              disabled={deadlineLocked}
              className={showErrors && invalid.deadline ? 'p-invalid' : undefined}
              onChange={(e) =>
                setDraft((d) => ({ ...d, deadline: toISODate(e.value ?? null) }))
              }
            />
            {showErrors && invalid.deadline && (
              <span className="act-form-err">{t('actions.form.required')}</span>
            )}
          </label>

          {/* Stratejik Hedef · Katkı (127) — OPSİYONEL, çoklu. Bu pencerede Dokümanlar bölümü yok;
              Bağımsız Görev Oluştur'daki "son alan" konumunun karşılığı olarak tarihin altında. Satır alanı
              hiç taşımıyorsa (Excel satırı) dokunulmadıkça tanımsız kalır → sunucu seçimi korur. */}
          <TaskClassificationFields
            options={levels}
            value={classificationValue}
            onChange={(next) => setDraft((d) => ({ ...d, ...changedDimensions(classificationValue, next) }))}
          />
        </div>
      </div>
    </Dialog>
  )
}

/**
 * Yalnız değişen boyutu döner (127). Satırın tanımsız alanı "sunucuda değişmedi" demektir (bkz.
 * `ActionFormRow.strategicObjectives`); kullanıcı yalnız Katkı'yı değiştirdiğinde dokunmadığı Stratejik Hedef
 * tanımsızdan `[]`'e dönseydi o görevin hedefleri sunucuda silinirdi. Bileşen dokunulmayan boyutu aynı dizi
 * referansıyla geri verdiği için referans karşılaştırması yeterlidir.
 */
function changedDimensions(before: TaskClassification, next: TaskClassification): Partial<TaskClassification> {
  return {
    ...(next.strategicObjectives === before.strategicObjectives ? {} : { strategicObjectives: next.strategicObjectives }),
    ...(next.contributions === before.contributions ? {} : { contributions: next.contributions }),
  }
}
